import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useSiteConfig } from "@/lib/siteConfig";

/**
 * Tells a customer how to open the documents they download.
 *
 * Their files are password-protected on the way out (see `core/pdf_lock.py`), which is worth
 * nothing if they find out by being refused by a PDF reader. So this says it twice, in the two
 * places people actually read: a standing line above the documents, and a toast at the moment
 * they click a download — the second because the first is the kind of banner eyes slide past
 * until it matters.
 *
 * Shown only when the lock is actually on. `pdf_lock` comes from `GET /site-config`, which
 * carries the flag but deliberately **not** the admin password (that would publish a shared
 * secret to every anonymous visitor); the only password this component ever names is the
 * customer's own mobile number, which is already theirs.
 *
 * The click detection is delegated from a wrapper rather than wired into each link: downloads are
 * plain `<a href>` in four different places here and in `FileRow`, which is also used by the
 * admin and advocate portals where no password applies.
 */
export default function DocumentPasswordNotice({ mobile }) {
  const { pdf_lock: locked } = useSiteConfig();
  const ref = useRef(null);
  const [toldThisVisit, setToldThisVisit] = useState(false);

  useEffect(() => {
    const root = ref.current?.parentElement;
    if (!root || !locked) return undefined;
    const onClick = (e) => {
      if (!e.target.closest?.('a[href*="/api/files/"]')) return;
      if (toldThisVisit) return;
      setToldThisVisit(true);
      // Says "without the country code" outright. The number is stored in two pieces now and the
      // code sits in its own field on the profile, so "your mobile number" had become genuinely
      // ambiguous for anyone ordering from abroad — and the password is the national number alone
      // (backend/core/phone.py, `customer_pdf_passwords`).
      toast.info(
        mobile
          ? "Use your registered mobile number — without the country code — to unlock the document."
          : "This document is password-protected. Add your mobile number in Profile, or contact support for the password.",
        { duration: 8000 });
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [locked, mobile, toldThisVisit]);

  if (!locked) return <span ref={ref} className="hidden" />;

  return (
    <div ref={ref} data-testid="document-password-notice"
         className="flex gap-2.5 items-start rounded-lg border border-[#CBA96C]/30 bg-[#CBA96C]/5 px-3 py-2.5">
      <Lock className="w-4 h-4 text-[#CBA96C] shrink-0 mt-0.5" strokeWidth={1.5} />
      <div className="text-xs leading-relaxed text-[#A0AABF]">
        {mobile ? (
          <>
            <span className="text-white/90">Your documents are password-protected.</span>{" "}
            Use your registered mobile number <span className="text-white/90">{mobile}</span> to
            unlock the document — digits only, no spaces or country code.
          </>
        ) : (
          <>
            <span className="text-white/90">Your documents are password-protected.</span>{" "}
            There is no mobile number on your profile, so you will need the password from support.{" "}
            <Link to="/customer/profile" data-testid="password-notice-profile"
                  className="underline text-[#CBA96C] hover:opacity-80">Add your mobile number</Link>{" "}
            and it will unlock your documents instead.
          </>
        )}
      </div>
    </div>
  );
}
